/**
 * A version's diff as a person reads it (§5.25): grouped by what it is about —
 * the workspace's settings, each project, the secret values — and each line
 * named in words rather than as a path.
 *
 * `repos[site].startCmd` is what the diff records, and it stays the identity of
 * the line: a publish ticks lines by it (`pick.ts`), so nothing here renames
 * one. What changes is what is drawn — *site · Run command* — because a person
 * choosing what to send was reading a document's field names. Every path and
 * value came off the network or out of a document and is only ever drawn as
 * text.
 */
import type { DiffEntry, DiffKind } from './diff';

export interface ChangeLine {
  /** The diff's own path — the line's identity, and what a selection holds. */
  path: string;
  kind: DiffKind;
  /** In words: `Run command`, `Variable LOG_LEVEL`, `Console call Login`. */
  label: string;
  before?: unknown;
  after?: unknown;
  /** The whole group's subject was added or removed by this line (a project, a call). */
  whole: boolean;
}

export interface ChangeGroup {
  /** Stable: `workspace`, `repo:<label>`, `secrets`, `sources`. */
  key: string;
  title: string;
  /** Where a folder of a monorepo is (§5.4) — `airlock/site` — or null. */
  folder: string | null;
  /** The project's colour, when the caller knows it. */
  color: string | null;
  lines: ChangeLine[];
}

/** What a group needs to know about a project that the diff does not carry. */
export interface ProjectLook {
  name: string;
  color?: string | null;
  subPath?: string | null;
  /** The repository it is a folder of, by name. */
  repository?: string | null;
}

const FIELDS: Record<string, string> = {
  name: 'Name',
  color: 'Colour',
  startCmd: 'Run command',
  installCmd: 'Install command',
  checkCmd: 'Check command',
  healthCheck: 'Health check',
  port: 'Port',
  portEnv: 'Port variable',
  packageManager: 'Package manager',
  runtime: 'Runtime',
  kind: 'What it serves',
  defaultBranch: 'Default branch',
  relPath: 'Folder',
  subPath: 'Folder inside the repository',
  cloneUrl: 'Remote',
  remote: 'Remote',
  host: 'Remote host',
  envFile: 'Environment file',
  tunnelDomain: 'Tunnel domain',
  tunnelAuto: 'Opens its tunnel on start',
  skipUnchangedInstall: 'Skips an unchanged install',
  sortOrder: 'Position'
};

const CONFIG: Record<string, string> = {
  defaultModel: 'Default model',
  defaultEffort: 'Default effort',
  defaultSourceBranch: 'Default source branch',
  defaultTargetBranch: 'Default target branch',
  dockerfile: 'Session image',
  extraDomains: 'Egress hosts',
  blockedDomains: 'Blocked hosts',
  hostServices: 'Services on this computer',
  localServices: 'Containers on your computer',
  rulesRepository: 'Rules repository',
  vmCpu: 'Session CPUs',
  vmMemoryMb: 'Session memory',
  vmDiskGb: 'Session disk',
  sandboxBackend: 'Where sessions run',
  egressMode: 'Egress',
  agentExtensions: 'Skills, MCP servers and plugins',
  autonomous: 'Autonomous agent',
  commitCoAuthoredBy: 'Co-authored-by trailer',
  privacyMask: 'Personal data mask',
  privacyTerms: 'Names to mask',
  autoLand: 'Landing by itself',
  audit: 'Audit',
  gitFormats: 'Branch and commit formats',
  containerRuntime: 'Container runtime',
  dockerDiskGb: 'Docker disk',
  archiveRetentionDays: 'Archive retention',
  logRetentionMb: 'Log retention',
  logBufferLines: 'Log buffer',
  idleHintMs: 'Idle hint',
  notifications: 'Notifications',
  agentUpdate: 'Agent updates',
  agentConfigSource: 'Agent configuration',
  autoSync: 'Auto sync'
};

const CONSOLE: Record<string, string> = {
  requests: 'Console call',
  folders: 'Console folder',
  environments: 'Console environment',
  variables: 'Console variable',
  flows: 'Console flow'
};

/** `vmMemoryMb` → `Vm memory mb`: the fallback for a key nothing above names. */
function words(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `[api]` and `.field` segments of a diff path, in order. */
function segments(path: string): { name: string; item: string | null }[] {
  const out: { name: string; item: string | null }[] = [];
  for (const match of path.matchAll(/([^.[\]]+)(?:\[([^\]]*)\])?/g))
    out.push({ name: match[1], item: match[2] ?? null });
  return out;
}

/** A project-level path in words: everything after `repos[<label>]`. */
function projectLabel(rest: { name: string; item: string | null }[]): string {
  const [first, second, third] = rest;
  if (!first) return 'the whole project';
  if (first.name === 'env' && first.item !== null) {
    const field = second ? ` — ${second.name === 'value' ? 'value' : words(second.name).toLowerCase()}` : '';
    return `Variable ${first.item}${field}`;
  }
  if (first.name === 'api' && second) {
    const noun = CONSOLE[second.name] ?? `Console ${words(second.name).toLowerCase()}`;
    const named = second.item !== null ? `${noun} ${second.item}` : noun;
    return third ? `${named} — ${words(third.name).toLowerCase()}` : named;
  }
  return FIELDS[first.name] ?? words(first.name);
}

/**
 * The diff, grouped. `projects` says what the diff cannot — a project's colour
 * and whether it is a folder of another's repository — and may be empty: a
 * group then has only its name.
 */
export function groupChanges(diff: readonly DiffEntry[], projects: readonly ProjectLook[] = []): ChangeGroup[] {
  const groups = new Map<string, ChangeGroup>();
  const group = (key: string, make: () => Omit<ChangeGroup, 'key' | 'lines'>): ChangeGroup => {
    let found = groups.get(key);
    if (!found) {
      found = { key, ...make(), lines: [] };
      groups.set(key, found);
    }
    return found;
  };
  const workspace = (): ChangeGroup =>
    group('workspace', () => ({ title: 'Workspace settings', folder: null, color: null }));

  for (const entry of diff) {
    const line = (label: string, whole = false): ChangeLine => ({
      path: entry.path,
      kind: entry.kind,
      label,
      before: entry.before,
      after: entry.after,
      whole
    });
    // A publish's secret writes, as a revision records them (`secretEntries`).
    if (entry.path.endsWith(' (secret)')) {
      const described = entry.path.slice(0, -' (secret)'.length);
      group('secrets', () => ({ title: 'Secret values', folder: null, color: null })).lines.push(
        line(secretLabel(described))
      );
      continue;
    }
    const [head, ...rest] = segments(entry.path);
    if (!head) continue;
    if (head.name === 'project') {
      workspace().lines.push(line('The whole workspace', true));
    } else if (head.name === 'repos' && head.item !== null) {
      const look = projects.find((project) => project.name === head.item);
      const target = group(`repo:${head.item}`, () => ({
        title: head.item ?? '',
        folder: look?.subPath ? `${look.repository ?? ''}/${look.subPath}`.replace(/^\//, '') : null,
        color: look?.color ?? null
      }));
      target.lines.push(line(projectLabel(rest), rest.length === 0));
    } else if (head.name === 'config') {
      const [key] = rest;
      workspace().lines.push(line(key ? (CONFIG[key.name] ?? words(key.name)) : 'Configuration'));
    } else if (head.name === 'taskSources') {
      group('sources', () => ({ title: 'Ticket sources', folder: null, color: null })).lines.push(
        line(head.item ?? 'A source', rest.length === 0)
      );
    } else if (head.name === 'name' || head.name === 'color') {
      workspace().lines.push(line(head.name === 'name' ? 'Workspace name' : 'Workspace colour'));
    } else {
      workspace().lines.push(line(words(head.name)));
    }
  }
  // The workspace first, then the projects in the order the diff met them, then what is not a project.
  const order = (key: string): number => (key === 'workspace' ? 0 : key.startsWith('repo:') ? 1 : 2);
  return [...groups.values()].sort((a, b) => order(a.key) - order(b.key));
}

/** A secret value's line — `repos[api].env[DATABASE_URL]` → `api · DATABASE_URL`. */
export function secretLabel(described: string): string {
  const [head, ...rest] = segments(described);
  if (head?.name !== 'repos' || head.item === null) return described;
  const leaf = rest.find((segment) => segment.item !== null);
  const suffix = described.match(/\(([^)]+)\)\s*$/)?.[1];
  return `${head.item} · ${leaf?.item ?? rest.map((segment) => segment.name).join('.')}${suffix ? ` (${suffix})` : ''}`;
}

/** Secret values a publish would seal, as one group of their own. */
export function secretGroup(secrets: readonly { path: string; label: string }[]): ChangeGroup | null {
  if (!secrets.length) return null;
  return {
    key: 'secrets',
    title: 'Secret values',
    folder: null,
    color: null,
    lines: secrets.map((secret) => ({
      path: secret.path,
      kind: 'changed' as const,
      label: secretLabel(secret.label),
      whole: false
    }))
  };
}

/** A diff line's path, in the words its group and line would say — for a conflict, which is drawn alone. */
export function describePath(path: string, projects: readonly ProjectLook[] = []): string {
  const [group] = groupChanges([{ path, kind: 'changed' }], projects);
  if (!group) return path;
  return group.key === 'workspace' ? group.lines[0].label : `${group.title} · ${group.lines[0].label}`;
}

/** The projects of a workspace or a document, as the groups need them. */
export function looksOf(
  projects: readonly {
    name: string;
    color?: string | null;
    subPath?: string | null;
    repoRoot?: string;
    remote?: string;
  }[]
): ProjectLook[] {
  return projects.map((project) => {
    const repository = project.subPath
      ? projects.find(
          (one) =>
            !one.subPath &&
            ((project.repoRoot !== undefined && one.repoRoot === project.repoRoot) ||
              (project.remote !== undefined && one.remote === project.remote))
        )
      : undefined;
    return {
      name: project.name,
      color: project.color ?? null,
      subPath: project.subPath ?? null,
      repository: repository?.name ?? null
    };
  });
}
